import React, { useState, useEffect } from 'react';
import {
    MdFileDownload,
    MdSearch,
    MdFilterList,
    MdExpandMore,
    MdExpandLess,
    MdInventory,
    MdBusinessCenter,
    MdFormatListNumbered,
    MdCurrencyRupee
} from 'react-icons/md';
import { toast } from 'react-toastify';
import styles from './ProjectSummaryReport.module.css';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import Pagination from '../components/common/Pagination';
import TableLimitSelector from '../components/common/TableLimitSelector';
import Drawer from '../components/common/Drawer';
import PaginatedSelect from '../components/common/PaginatedSelect';
import { authFetch } from '../components/utils/authFetch';
import { GET_PROJECT_PRODUCT_SUMMARY_API, GET_ALL_PROJECT_API } from '../config/API';

const ProjectProductSummaryReport = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [showStats, setShowStats] = useState(window.innerWidth > 768);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (!mobile) setShowStats(true);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };

    const fetchReports = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?page=${currentPage}&limit=${limit}&search=${debouncedSearch}`;

            if (selectedProject?.value) {
                queryParams += `&projectName=${selectedProject.value}`;
            }

            const response = await authFetch(
                `${GET_PROJECT_PRODUCT_SUMMARY_API}${queryParams}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const result = await response.json();

            if (result.status) {
                setReports(result.data || []);
                const pagination = result.pagination;
                setTotalPages(pagination?.totalPages || 1);
                setTotalRecords(pagination?.totalCount || result.data?.length || 0);
            } else {
                toast.error(result.message || 'Failed to fetch project product summary');
                setReports([]);
            }
        } catch (error) {
            console.error('Error fetching project product summary:', error);
            toast.error('An error occurred while fetching the report');
            setReports([]);
        } finally {
            setLoading(false);
        }
    };

    const handleExportCSV = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?exportCSV=true&search=${debouncedSearch}`;
            if (selectedProject?.value) {
                queryParams += `&projectId=${selectedProject.value}`;
            }

            const response = await authFetch(`${GET_PROJECT_PRODUCT_SUMMARY_API}${queryParams}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const result = await response.json();

            if (result.status) {
                toast.success(result.message || 'Export started! Check Report Log for the file.');
            } else {
                toast.error(result.message || 'Failed to start export');
            }
        } catch (error) {
            console.error('Error exporting CSV:', error);
            toast.error('An error occurred during export');
        }
    };

    useEffect(() => {
        fetchReports();
    }, [currentPage, limit, debouncedSearch, selectedProject]);

    const getStatusClass = (status) => {
        const s = status?.toUpperCase();
        if (s === 'ACTIVE' || s === 'IN PROGRESS') return styles['status-in-progress'];
        if (s === 'APPROVED' || s === 'COMPLETED') return styles['status-approved'];
        if (s === 'PENDING') return styles['status-pending'];
        if (s === 'ARCHIVED') return styles['status-archived'];
        if (s === 'DELAYED') return styles['status-delayed'];
        return '';
    };

    const formatCurrency = (val) => {
        const amount = parseFloat(val) || 0;
        return `₹${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <div className={styles['purchase-orders-container']}>
            {/* Header Section */}
            <div className={styles['purchase-orders-header']}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div>
                        <h2>Project Product Summary Report</h2>
                        <p className={styles['page-subtitle']}>
                            Detailed breakdown of product items, quantities, rates, and total costs per project.
                        </p>
                    </div>
                </div>
                <div className={styles['header-actions']}>
                    <button
                        className={`${styles['filter-btn']} ${showFilters ? styles.active : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <MdFilterList /> Filter {selectedProject ? '(1)' : ''}
                    </button>
                    <button className={styles['export-btn']} onClick={handleExportCSV}>
                        <MdFileDownload size={18} /> Export CSV
                    </button>
                </div>
            </div>

            {/* Filter Drawer */}
            <Drawer
                isOpen={showFilters}
                onClose={() => setShowFilters(false)}
                title="Filter Reports"
            >
                <div className={styles['drawer-filter-content']}>
                    <div className={styles['filter-item']}>
                        <label>Project Name</label>
                        <PaginatedSelect
                            apiUrl={GET_ALL_PROJECT_API}
                            mapResponse={(p) => ({
                                value: p.id || p._id,
                                label: p.name || p.projectName
                            })}
                            value={selectedProject}
                            onChange={(option) => setSelectedProject(option)}
                            placeholder="Select Project"
                            isClearable
                        />
                    </div>
                    <div className={styles['drawer-actions']}>
                        <button
                            className={styles['clear-filters-btn']}
                            onClick={() => {
                                setSelectedProject(null);
                            }}
                            style={{ width: '100%' }}
                        >
                            Reset Filter
                        </button>
                    </div>
                </div>
            </Drawer>

            {/* Mobile Stats Toggle */}
            {isMobile && (
                <button
                    className={styles['stats-toggle-btn']}
                    onClick={() => setShowStats(!showStats)}
                >
                    {showStats ? (
                        <>
                            <MdExpandLess /> Hide Summary
                        </>
                    ) : (
                        <>
                            <MdExpandMore /> Show Summary
                        </>
                    )}
                </button>
            )}

            {/* Stats Grid */}
            <div className={`${styles['stats-grid']} ${showStats ? styles['show'] : styles['hide']}`}>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['blue']}`}>
                            <MdFormatListNumbered />
                        </div>
                    </div>
                    <div className={styles['stat-title']}>Total Items</div>
                    <div className={styles['stat-value']}>{totalRecords}</div>
                </div>

                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['purple']}`}>
                            <MdBusinessCenter />
                        </div>
                    </div>
                    <div className={styles['stat-title']}>Active Projects</div>
                    <div className={styles['stat-value']}>
                        {new Set(reports.map(r => r.projectId)).size}
                    </div>
                </div>

                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['orange']}`}>
                            <MdInventory />
                        </div>
                    </div>
                    <div className={styles['stat-title']}>Products Count</div>
                    <div className={styles['stat-value']}>
                        {new Set(reports.map(r => r.productId)).size}
                    </div>
                </div>

                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['green']}`}>
                            <MdCurrencyRupee />
                        </div>
                    </div>
                    <div className={styles['stat-title']}>Page Total</div>
                    <div className={styles['stat-value']}>
                        {formatCurrency(reports.reduce((acc, curr) => acc + (parseFloat(curr.total) || 0), 0))}
                    </div>
                </div>
            </div>

            {/* Main Content Table Card */}
            <div className={!isMobile ? styles['card-container-po'] : ''}>
                <div className={styles['search-container']}>
                    <TableLimitSelector limit={limit} onLimitChange={handleLimitChange} />
                    <div className={styles['search-wrapper']}>
                        <MdSearch className={styles['search-icon']} />
                        <input
                            type="text"
                            placeholder="Search by project name or product name"
                            className={styles['search-input']}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {!isMobile ? (
                    <div className={styles['table-container']}>
                        <table>
                            <thead>
                                <tr>
                                    <th>S.N.</th>
                                    <th>Project Details</th>
                                    <th>Start Date</th>
                                    <th>Status</th>
                                    <th>Product / Description</th>
                                    <th>Size</th>
                                    <th className={styles['text-center']}>Qty</th>
                                    <th className={styles['text-right']}>Rate</th>
                                    <th className={styles['text-right']}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="9" style={{ textAlign: 'center', padding: '3rem' }}>
                                            <Loader text="Fetching project product summary..." />
                                        </td>
                                    </tr>
                                ) : reports.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" style={{ textAlign: 'center', padding: '3rem' }}>
                                            <NoData title="No Reports Found" message="Try adjusting your search filters." />
                                        </td>
                                    </tr>
                                ) : (
                                    reports.map((item, idx) => (
                                        <tr key={item.sn || idx}>
                                            <td>{item.sn}</td>
                                            <td>
                                                <div className={styles['project-name-cell']} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                                                    <span>{item.projectName}</span>
                                                    {item.projectAdd && (
                                                        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>
                                                            {item.projectAdd}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>{formatDate(item.startDate)}</td>
                                            <td>
                                                <span className={`${styles['status-badge']} ${getStatusClass(item.status)}`}>
                                                    {item.status}
                                                </span>
                                            </td>
                                            <td>
                                                <div>
                                                    {item.productName && item.productName !== 'Unknown' && (
                                                        <div style={{ fontWeight: 600 }}>{item.productName}</div>
                                                    )}
                                                    <div>{item.description || '-'}</div>
                                                </div>
                                            </td>
                                            <td>{item.size || '-'}</td>
                                            <td className={styles['text-center']}>{item.qty}</td>
                                            <td className={styles['text-right']}>{formatCurrency(item.rate)}</td>
                                            <td className={`${styles['text-right']} ${styles['amount-cell-grandTotal']}`}>
                                                {formatCurrency(item.total)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className={styles['mobile-card-view']}>
                        {loading ? (
                            <div style={{ padding: '2rem' }}>
                                <Loader text="Fetching reports..." />
                            </div>
                        ) : reports.length === 0 ? (
                            <NoData title="No Reports Found" message="Try adjusting your filters." />
                        ) : (
                            reports.map((item, idx) => (
                                <div key={item.sn || idx} className={styles['project-card']}>
                                    <div className={styles['card-header']}>
                                        <div className={styles['card-title']}>
                                            <strong>{item.projectName}</strong>
                                            <span className={`${styles['status-badge']} ${getStatusClass(item.status)}`}>
                                                {item.status}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={styles['card-body']}>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>S.N.:</span>
                                            <span className={styles['card-value']}>#{item.sn}</span>
                                        </div>
                                        {item.projectAdd && (
                                            <div className={styles['card-row']}>
                                                <span className={styles['card-label']}>Address:</span>
                                                <span className={styles['card-value']}>{item.projectAdd}</span>
                                            </div>
                                        )}
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Description:</span>
                                            <span className={styles['card-value']}>{item.description || '-'}</span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Size:</span>
                                            <span className={styles['card-value']}>{item.size || '-'}</span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Qty:</span>
                                            <span className={styles['card-value']}>{item.qty}</span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Rate:</span>
                                            <span className={styles['card-value']}>{formatCurrency(item.rate)}</span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Total:</span>
                                            <span className={`${styles['card-value']} ${styles['amount-cell-grandTotal']}`}>
                                                {formatCurrency(item.total)}
                                            </span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Start Date:</span>
                                            <span className={styles['card-value']}>{formatDate(item.startDate)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                <Pagination
                    limit={limit}
                    onLimitChange={handleLimitChange}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    onPageChange={(page) => setCurrentPage(page)}
                    currentCount={reports.length}
                    entityName="Product Summary"
                />
            </div>
        </div>
    );
};

export default ProjectProductSummaryReport;
